import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../services/api';
import SessionManager from '../utils/sessionManager';
import SuperAdminLayout from '../components/layouts/SuperAdminLayout';
import Icon from '../components/atoms/Icon';

const SuperAdminPaymentWebhooks = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [webhooks, setWebhooks] = useState([]);

  const currentSession = SessionManager.getCurrentSession();
  const adminId = currentSession?.user_id || currentSession?.profile?.id;

  useEffect(() => {
    if (!currentSession || currentSession.role !== 'super_admin') {
      navigate('/');
    }
  }, [currentSession, navigate]);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      setLoading(true);
      const data = await request(`/super-admin/payments/webhooks?admin_id=${adminId}&limit=100`);
      setWebhooks(data);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <SuperAdminLayout>
      <div className="h-full overflow-auto bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Webhook Events</h1>
              <p className="text-sm text-gray-600 mt-1">Monitor Razorpay webhook events and their processing status</p>
            </div>
            <button
              onClick={fetchWebhooks}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Icon name="refresh" className="mr-2 text-lg" />
              Refresh
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Webhooks Table */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
              </div>
            ) : webhooks.length === 0 ? (
              <div className="text-center py-20">
                <Icon name="webhook" className="text-gray-300 text-6xl mb-4" />
                <p className="text-gray-500 text-lg font-medium">No webhook events</p>
                <p className="text-gray-400 text-sm mt-1">Webhook events will appear here when received</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-700 uppercase tracking-wider">Time</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-700 uppercase tracking-wider">Event Types</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-700 uppercase tracking-wider">Event ID</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-700 uppercase tracking-wider">Verified</th>
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-700 uppercase tracking-wider">Processed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {webhooks.map((webhook) => (
                      <tr key={webhook.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-6 text-sm text-gray-600 whitespace-nowrap">
                          {formatDate(webhook.created_at)}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center">
                            <Icon name="event" className="text-blue-500 mr-2 text-lg" />
                            <span className="text-sm font-medium text-gray-900">{webhook.event_type}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center">
                            <Icon name="fingerprint" className="text-gray-400 mr-2 text-sm" />
                            <span className="text-xs font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">
                              {webhook.razorpay_event_id}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          {webhook.signature_verified ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                              <Icon name="verified" className="mr-1 text-sm" />
                              Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-200">
                              <Icon name="error" className="mr-1 text-sm" />
                              Not Verified
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-6">
                          {webhook.processed ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                              <span className="w-1.5 h-1.5 bg-green-600 rounded-full mr-2"></span>
                              Processed
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                              <span className="w-1.5 h-1.5 bg-yellow-600 rounded-full mr-2"></span>
                              Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Summary */}
          {webhooks.length > 0 && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Events</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{webhooks.length}</p>
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="webhook" className="text-blue-600 text-xl" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Verified</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {webhooks.filter(w => w.signature_verified).length}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="verified" className="text-green-600 text-xl" />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Processed</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {webhooks.filter(w => w.processed).length}
                    </p>
                  </div>
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name="check_circle" className="text-green-600 text-xl" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminPaymentWebhooks;
